/**
 * Namespaced localStorage wrapper.
 *
 * All app-owned keys are prefixed with `opmode_` to prevent collisions with
 * third-party scripts (Firebase auth, RevenueCat, embedded widgets) and to
 * make app state easy to identify in DevTools.
 *
 * Use `storage.get/set/remove` for any app-owned key. For third-party keys
 * you do NOT own (e.g. TensorFlow's own `tensorflowjs_models/...` entries),
 * call `localStorage` directly.
 *
 * A one-time migration renames legacy unprefixed keys on boot; see
 * {@link migrateLegacyStorage}.
 */

const PREFIX = 'opmode_';
const MIGRATION_FLAG = 'opmode_storage_migration_v1';

/**
 * Known app-owned keys. Adding a new key? Put it here so the typed API picks
 * it up and so future migrations know to carry it forward.
 */
export const StorageKeys = [
  // Core state
  'tacticalFitnessState',
  'userProgramSelection',
  'userProfile',
  'userName',
  'currentProgram',
  'programStartDate',

  // Active workout
  'activeSessionBackup',
  'freestyleSessionBackup',
  'deloadAdjustment',
  'postWorkoutImpact',

  // 1RM
  'oneRMs',
  'oneRMHistory',
  'estimated1RMs',
  'pending1RMSuggestions',
  'autoApply1RMSuggestions',

  // History / metrics
  'sessionHistory',
  'recoveryHistory',
  'fitnessTests',
  'battleReadinessHistory',

  // Errors / bug reports
  'errorLogs',
  'lastAutoSavedBug',
  'pendingBugReports',

  // Sync / migration flags
  'dexie_migration_complete',
  'programSelectionBackfillComplete',
  'lastSync_sessions',
  'lastSync_recovery',
  'lastSync_assessments',
  'lastStoragePrune',

  // UI prefs
  'autoCollapseEnabled',
  'setBySetMode',

  // Operations / gameplay
  'operationArchetype',

  // ML
  'ml-model-version',

  // Added in straggler pass
  'intel_backfill_complete_v2',   // bare legacy; dynamic usage is prefixed via pattern
  'googlefit_token',
  'opmode-load-model-meta',
  'override_events',
  'skippedDays',
  'customProgramDraftV2',
  'customQuickDraft',
  'spearhead-world-events-seen',
  'metrics-ops-active-tab',
  'metrics-active-view',
  'metrics-analytics-subtab',
  'emergencySessionBackup',
  'pendingFirebaseSessions',

  // Added with deload banner (24h dismiss cooldown)
  'deload_banner_dismissed_at',
] as const;

/**
 * Patterns for dynamically-keyed legacy entries (e.g. `seenCosmetics_${uid}`).
 * The migration walks localStorage once and renames every key that matches one
 * of these patterns.
 */
const LEGACY_KEY_PATTERNS: RegExp[] = [
  /^seenCosmetics_/,
  /^gunfighter_crosssell_dismissed_/,
  /^program_cache_/,
  /^intel_backfill_complete_v2_/,
  /^spearhead-prev-control-/,
  /^full_firebase_download_/,
  /^dashboard-collapse-/,
];

export const storage = {
  get(key: string): string | null {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(PREFIX + key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(PREFIX + key, value);
    } catch {
      // Quota exceeded or storage unavailable — fail silently; callers
      // treat localStorage as best-effort.
    }
  },
  remove(key: string): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(PREFIX + key);
    } catch {
      // Ignore
    }
  },
};

let migrationRan = false;

/**
 * Rename all legacy (unprefixed) localStorage keys to the `opmode_` namespace.
 * Idempotent — sets a flag after first run and short-circuits thereafter.
 * Safe to call before any other storage access; runs only in the browser.
 */
export function migrateLegacyStorage(): void {
  if (migrationRan || typeof window === 'undefined') return;
  migrationRan = true;

  try {
    if (window.localStorage.getItem(MIGRATION_FLAG) === 'true') return;

    for (const key of StorageKeys) {
      const legacyValue = window.localStorage.getItem(key);
      if (legacyValue === null) continue;
      const prefixed = PREFIX + key;
      if (window.localStorage.getItem(prefixed) === null) {
        window.localStorage.setItem(prefixed, legacyValue);
      }
      window.localStorage.removeItem(key);
    }

    const existingKeys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k) existingKeys.push(k);
    }
    for (const key of existingKeys) {
      if (key.startsWith(PREFIX)) continue;
      if (!LEGACY_KEY_PATTERNS.some((p) => p.test(key))) continue;
      const value = window.localStorage.getItem(key);
      if (value === null) continue;
      const prefixed = PREFIX + key;
      if (window.localStorage.getItem(prefixed) === null) {
        window.localStorage.setItem(prefixed, value);
      }
      window.localStorage.removeItem(key);
    }

    window.localStorage.setItem(MIGRATION_FLAG, 'true');
  } catch {
    // Migration is best-effort: if localStorage is unavailable or throws,
    // the app still boots and writes proceed with the prefixed keys.
  }
}

// Run migration on first client import. Placed at module scope so it fires
// before any component's useEffect can read from localStorage via `storage`.
if (typeof window !== 'undefined') {
  migrateLegacyStorage();
}
